"use client"

import Image from "next/image"
import { CSSProperties, useEffect, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"

import { RelatedProductCardData } from "./card-data"
import styles from "./related-products.module.css"

const SLIDE_INTERVAL_MS = 1200
// Blur radii grow geometrically (~x1.7 per layer) up to the base of the veil.
// Each layer fades in over a wide band that overlaps its neighbours, so the
// ramp stays smooth even though the veil is short.
const BLUR_RADII = [0.5, 0.9, 1.5, 2.5, 4.2, 7, 11.5, 19]
const BLUR_BAND_STEP = 9
const BLUR_BAND_WIDTH = 37

const BLUR_LAYERS = BLUR_RADII.map((blur, index) => ({
  blur,
  from: index * BLUR_BAND_STEP,
  to: index * BLUR_BAND_STEP + BLUR_BAND_WIDTH,
}))

type RelatedProductCardProps = {
  product: RelatedProductCardData
}

export default function RelatedProductCard({
  product,
}: RelatedProductCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [activeImage, setActiveImage] = useState(0)
  const imageCount = product.images.length

  useEffect(() => {
    if (
      !isHovered ||
      imageCount < 2 ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return
    }

    const interval = window.setInterval(
      () => setActiveImage((current) => (current + 1) % imageCount),
      SLIDE_INTERVAL_MS
    )

    return () => window.clearInterval(interval)
  }, [imageCount, isHovered])

  const stopSlideshow = () => {
    setIsHovered(false)
    setActiveImage(0)
  }

  return (
    <LocalizedClientLink
      className={styles.card}
      href={`/products/${product.handle}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={stopSlideshow}
    >
      {product.images.map((url, imageIndex) => (
        <Image
          alt={imageIndex === 0 ? product.title : ""}
          className={`${styles.image} ${
            imageIndex === activeImage ? styles.imageActive : ""
          }`}
          draggable={false}
          fill
          key={url}
          sizes="25vw"
          src={url}
        />
      ))}

      <span aria-hidden="true" className={styles.tint} />
      {BLUR_LAYERS.map(({ blur, from, to }) => (
        <span
          aria-hidden="true"
          className={styles.blurLayer}
          key={blur}
          style={
            {
              "--rp-blur": `${blur}px`,
              "--rp-from": `${from}%`,
              "--rp-to": `${to}%`,
            } as CSSProperties
          }
        />
      ))}

      <span className={styles.info}>
        <span className={styles.name}>{product.title}</span>
        {product.price && <span className={styles.price}>{product.price}</span>}
        {product.sizes.length > 0 && (
          <span className={styles.sizes}>
            {product.sizes.map((size) => (
              <span className={styles.size} key={size}>
                {size}
              </span>
            ))}
          </span>
        )}
      </span>
    </LocalizedClientLink>
  )
}
